<template>
  <div
    class="c-header-search"
    :class="{ 'is-expanded': isHighlightsPanelVisible }"
    @keyup.esc="setIsHighlightsPanelVisible(false)"
  >
    <form
      ref="form"
      class="c-header-search__container"
      :action="formAction"
      role="search"
      autocomplete="off"
      @submit.prevent="hasValidTerm && submitForm()"
    >
      <div class="c-header-search__header">
        <div class="o-grid -xs-middle -xs-gutter-medium">
          <div class="o-grid__item -lg-grow">
            <div class="c-header-search__field">
              <component
                :is="isMobile ? 'div' : 'button'"
                class="c-header-search__field-submit-button"
                :type="isDesktop && 'submit'"
                aria-label="Pesquisar"
                :aria-hidden="isMobile"
                data-test="button-filtro-pesquisa"
              >
                <vIcon
                  name="search"
                  color="primary"
                />
              </component>

              <button
                v-cloak
                type="button"
                class="c-header-search__field-clean-button"
                :class="{
                  'is-hidden': !term.length
                }"
                @click="clear"
              >
                <vIcon
                  name="cross"
                  color="primary"
                  size="xxsmall"
                />
              </button>

              <input
                ref="input"
                v-model.trim="term"
                data-test="input-filtro-pesquisa"
                type="search"
                name="query"
                autocomplete="off"
                autocapitalize="off"
                spellcheck="false"
                autocorrect="off"
                class="o-input -xs-medium t-fill"
                aria-label="busca"
                :placeholder="`Busque ${placeholder}`"
                @blur="!isMobile && setIsHighlightsPanelVisible(false)"
                @mousedown="handleInputClick"
                @input="handleInput"
              >

              <input
                v-if="isProductContext"
                type="hidden"
                name="ref"
                :value="productsSearchRef"
              >
            </div>
          </div>

          <div class="o-grid__item -xs-shrink c-header-search__cancel">
            <button
              type="button"
              class="o-text -xs-semibold -xs-brevier -xs-primary"
              @click="cancel"
            >
              cancelar
            </button>
          </div>
        </div>
      </div>

      <div
        v-if="!isLargeDesktop"
        class="c-header-search__expansion"
      >
        <vSearchHighlightsContent
          :initial-context="initialContext"
        />
      </div>
    </form>
  </div>
</template>

<script>
  import { mapMutations, mapGetters, mapActions } from 'vuex'
  import STORE_TYPES_SEARCH from '@/web/store/registered/search'
  import STORE_TYPES_REQUEST from '@/web/store/registered/request'

  import media from '@/tools/mixins/media'
  import RouteParser from '@/tools/route-parser'
  import updateHistory from '@/tools/update-history'
  import { PRODUCTS_SEARCH_CONTEXT } from '@/tools/constants/contexts-for-metrics'
  import { USER_SEARCH_CONTEXT, IN_USER_STORE_CONTEXTS } from '@/web/components/header-search/context-selector/constants'

  import vIcon from '@/web/components/icon.vue'
  import vSearchHighlightsContent from '@/web/components/header-search/search-highlights-content.vue'

  import '@engines/stylesheets/ui/objects/_text.scss'
  import '@engines/stylesheets/ui/objects/_grid.scss'
  import '@engines/stylesheets/ui/objects/_input.scss'
  import '@engines/stylesheets/ui/trumps/_overrides.scss'

  const MIN_TERM_LENGTH = 3
  const PLACEHOLDER_COMPLEMENT_LOOP_DELAY = 5000
  const PLACEHOLDER_COMPLEMENT_LETTER_ANIMATION_DELAY = 75
  const NAVIGATION_TYPE_BACK_FORWARD = 'back_forward'

  export default {
    components: {
      vIcon,
      vSearchHighlightsContent
    },
    mixins: [
      media
    ],
    props: {
      placeholderComplements: {
        type: Array,
        default: () => ([])
      },
      placeholderStore: {
        type: String,
        default: ''
      },
      shouldAutoFocusOnDesktop: {
        type: Boolean,
        default: true
      },
      initialContext: {
        type: String,
        default: ''
      }
    },
    data() {
      return {
        term: '',
        placeholderComplement: '',
        productsSearchRef: PRODUCTS_SEARCH_CONTEXT.PRODUCTS_SEARCH
      }
    },
    computed: {
      ...mapGetters({
        shouldPreventRedirect: STORE_TYPES_SEARCH.GETTERS.GET_SHOULD_PREVENT_REDIRECT,
        appliedTerm: STORE_TYPES_SEARCH.GETTERS.GET_TERM,
        controllerName: STORE_TYPES_REQUEST.GETTERS.GET_CONTROLLER,
        actionName: STORE_TYPES_REQUEST.GETTERS.GET_ACTION,
        selectedContext: STORE_TYPES_SEARCH.GETTERS.GET_SELECTED_CONTEXT,
        isProductContext: STORE_TYPES_SEARCH.GETTERS.GET_IS_PRODUCT_CONTEXT,
        isHighlightsPanelVisible: STORE_TYPES_SEARCH.GETTERS.GET_IS_HIGHLIGHTS_PANEL_VISIBLE
      }),
      formAction() {
        switch (this.selectedContext) {
        case USER_SEARCH_CONTEXT: return '/busca/lojinha'
        default: return '/s'
        }
      },
      hasValidTerm() {
        return this.term.length >= MIN_TERM_LENGTH
      },
      isStorePage() {
        return `${this.controllerName}.${this.actionName}` === 'users.show'
      },
      isOfficialStoresPage() {
        return `${this.controllerName}.${this.actionName}` === 'official_stores.index'
      },
      placeholder() {
        return this.isStoreMobile ? this.placeholderStore : `"${this.placeholderComplement}"`
      },
      isStoreMobile() {
        return this.isStorePage && this.isMobile
      },
      isBackNavigation() {
        const navigation = window.performance.getEntriesByType('navigation')[0]
        return navigation?.type === NAVIGATION_TYPE_BACK_FORWARD
      },
      shouldFocusInput() {
        return !this.isMobile || this.isHighlightsPanelVisible || this.term
      }
    },
    watch: {
      appliedTerm: {
        handler() {
          if (!this.isStorePage) {
            this.term = this.appliedTerm
          }
        },
        immediate: true
      },
      isMobile: {
        handler() {
          if (this.isStoreMobile) {
            this.setSelectedContext(IN_USER_STORE_CONTEXTS.store)
            this.term = this.appliedTerm
            return
          }
          if (this.isStorePage) { this.term = '' }
          this.setSelectedContext(this.initialContext)
        },
        immediate: true
      },
      term(term) {
        this.setInputTerm(term)
        if (this.isStoreMobile && term.length === 0 && term !== this.appliedTerm) {
          this.changeTerm({ term: this.term, shouldSetShippingRange: false })
        }
      }
    },
    mounted() {
      this.placeholderComplement = this.randomPlaceholderComplement()
      setTimeout(() => this.loopPlaceholderComplements(), PLACEHOLDER_COMPLEMENT_LOOP_DELAY)
      this.shouldAutoFocusOnDesktop && this.isDesktop && this.focus()
    },
    methods: {
      ...mapActions({
        changeTerm: STORE_TYPES_SEARCH.ACTIONS.CHANGE_TERM
      }),
      ...mapMutations({
        setInputTerm: STORE_TYPES_SEARCH.MUTATIONS.SET_INPUT_TERM,
        setSelectedContext: STORE_TYPES_SEARCH.MUTATIONS.SET_SELECTED_CONTEXT,
        setIsHighlightsPanelVisible: STORE_TYPES_SEARCH.MUTATIONS.SET_IS_HIGHLIGHTS_PANEL_VISIBLE
      }),
      handleInputClick(event) {
        if (this.shouldFocusInput) {
          this.focus()
        } else {
          event.preventDefault()
        }
        this.setIsHighlightsPanelVisible(true)
      },
      cancel() {
        this.setIsHighlightsPanelVisible(false)
        this.term = this.appliedTerm
      },
      clear() {
        this.term = ''
        this.focus()
        if (!this.shouldRedirectOnSubmit()) {
          const shouldSetShippingRange = !(this.isStoreMobile)
          this.changeTerm({ term: this.term, shouldSetShippingRange })
        }
      },
      focus() {
        if (!this.isBackNavigation)
          this.$nextTick(() => this.$refs.input.focus())
      },
      setContextAndKeepFocus(context) {
        this.selectedContext = context
        this.focus()
      },
      submitForm() {
        if (!this.shouldRedirectOnSubmit()) {
          const shouldSetShippingRange = !(this.isStoreMobile)
          this.changeTerm({ term: this.term, shouldSetShippingRange })
          this.setIsHighlightsPanelVisible(false)
          this.updateQueryParamsRef()
          this.$refs.input.blur()
        } else {
          this.$refs.form.submit()
        }
      },
      shouldRedirectOnSubmit() {
        return this.selectedContext === USER_SEARCH_CONTEXT ||
          this.selectedContext === IN_USER_STORE_CONTEXTS.all ||
          !this.shouldPreventRedirect
      },
      updateQueryParamsRef() {
        const routeParser = new RouteParser(location.pathname)
        const params = routeParser.toObject(location.pathname + location.search)
        params.ref = this.productsSearchRef
        updateHistory(routeParser.toString(params), { method: 'replace' })
      },
      loopPlaceholderComplements() {
        this.erasePlaceholderComplement().then(() => {
          this.writePlaceholderComplement(this.randomPlaceholderComplement()).then(() => {
            setTimeout(() => {
              this.loopPlaceholderComplements()
            }, PLACEHOLDER_COMPLEMENT_LOOP_DELAY)
          })
        })
      },
      erasePlaceholderComplement() {
        return new Promise((resolve, reject) => {
          const eraseInterval = setInterval(function removeLetter() {
            this.placeholderComplement = this.placeholderComplement.slice(0, -1)
            if (!this.placeholderComplement.length) {
              clearInterval(eraseInterval)
              return resolve()
            }
            return removeLetter.bind(this)
          }.apply(this), PLACEHOLDER_COMPLEMENT_LETTER_ANIMATION_DELAY)
        })
      },
      writePlaceholderComplement(newPlaceholderComplement) {
        return new Promise((resolve, reject) => {
          const writeInterval = setInterval(function addLetter() {
            this.placeholderComplement = this.placeholderComplement + newPlaceholderComplement.slice(0, 1)
            newPlaceholderComplement = newPlaceholderComplement.slice(1)
            if (!newPlaceholderComplement.length) {
              clearInterval(writeInterval)
              return resolve()
            }
            return addLetter.bind(this)
          }.apply(this), PLACEHOLDER_COMPLEMENT_LETTER_ANIMATION_DELAY)
        })
      },
      randomPlaceholderComplement() {
        return this.placeholderComplements[Math.floor((Math.random() * this.placeholderComplements.length))]
      },
      handleInput({ target }) {
        this.setIsHighlightsPanelVisible(true)
        this.term = this.sanitizeTerm(target.value)
      },
      sanitizeTerm(termString) {
        return termString.replace(/[\/\[\]\(\)\\+?.*´^~\{\}\|]/gi, '')
      }
    }
  }
</script>
<style lang="scss">
  .c-header-search {
    $root: &;

    text-align: left;
    padding-bottom: 20px;

    @include breakpoint(lg) {
      padding-bottom: 0;
    }

    &__container {
      @include breakpoint($until: lg) {
        position: static;
        transform: translateY(0);
        background: rgba($color-white, 0);
        top: 70px;
        left: 0;
        transition: all 0s;
      }

      @include breakpoint(lg) {
        position: relative;
      }
    }

    &__field {
      position: relative;

      @include breakpoint(lg) {
        max-width: 400px;
        box-shadow: 0 0 0 rgba($color-black, 0);
        transition: max-width .25s ease 0s,
                    box-shadow .25s ease 0s;
      }
      // In mobile the button with icon is on the left,
      // while in desktop the icon is on the right.
      // On mobile it is also not supposed to be clickable.
      &-submit-button {
        position: absolute;
        left: 12px;
        top: 10px;

        @include breakpoint(lg) {
          left: auto;
          right: 20px;
          top: 12px;
        }
      }

      &-clean-button {
        position: absolute;
        right: 12px;
        top: 10px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: $color-neutral-300;
        border-radius: 100%;
        opacity: 1;
        transition: opacity .2s,
                    visibility 0s;

        @include breakpoint(lg) {
          display: none;
        }

        &.is-hidden {
          visibility: hidden;
          opacity: 0;
          transition: opacity .2s,
                      visibility 0s ease .2s;
        }
      }

      .o-input {
        padding-left: 48px;
        padding-right: 44px;
        height: 44px;
        border: 1.5px solid $color-neutral-600;
        border-radius: 16px;
        font-weight: $font-weight-medium;
        font-size: 16px;
        line-height: 20px;
        color: $color-text-extra-dark;

        @include breakpoint(lg) {
          height: 48px;
          padding-left: 20px;
          padding-right: 60px;
        }

        &:hover {
          border-color: $color-primary;
        }

        &.o-input:focus {
          box-shadow: none;
        }

        &::placeholder {
          color: $color-neutral-700;
          text-transform: lowercase;
        }
      }
    }

    &__cancel {
      max-width: 0;
      overflow: hidden;
      opacity: 0;
      transition: opacity .25s ease 0s,
                  max-width .25s ease 0s,
                  padding .25s ease 0s;
      padding-left: 0 !important;
      padding-right: 0 !important;

      @include breakpoint(lg) {
        display: none;
      }
    }

    &__expansion {
      @include scrollbar($margin: 4px);

      -webkit-overflow-scrolling: touch;

      @include breakpoint($until: lg) {
        opacity: 0;
        position: absolute;
        left: -200vh;
        transition: opacity .25s ease .25s;
        visibility: hidden;
      }

      @include breakpoint(lg) {
        background: $color-white;
        border: 1px solid transparent;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        box-shadow: 0 0 0 rgba($color-black, 0);
        position: absolute;
        z-index: get-z-index(dropdown);
        top: calc(100% - 1px);
        left: 0;
        width: 100%;
        overflow: hidden;
        opacity: 0;
        max-height: 80vh;
        visibility: hidden;
        transition: opacity .25s ease 0s,
                    box-shadow .25s ease 0s,
                    visibility 0s ease .25s;
      }
    }

    &__block {
      transition: padding .2s;

      @include breakpoint(lg) {
        margin-left: 74px;
      }

      > * {
        transition: opacity .2s;
      }

      &-header {
        margin-bottom: 20px;

        @include breakpoint(lg) {
          margin-bottom: 16px;
        }
      }

      &-title {
        @include breakpoint(lg) {
          font-size: 16px;
          line-height: 24px;
          font-weight: $font-weight-medium;
          color: $color-neutral-800;
        }
      }

      &.-lateral-padded {
        @include breakpoint($until: lg) {
          margin-top: 16px;
          padding-left: 20px;
          padding-right: 20px;
        }
      }

      &-suggestion {
        width: 100%;
        padding: 16px 5px;

        @include breakpoint($until: md) {
          padding-top: 14px;
          padding-bottom: 14px;
        }

        &-seller {
          display: flex;
          align-items: center;
          padding: 10px 0 10px 5px;
          width: 100%;

          &-avatar-image {
            overflow: clip;
            overflow-clip-margin: content-box;
            width: 32px;
            height: 32px;
            border-radius: 100%;
            margin-right: 12px;
          }
        }
      }

      &.is-hidden {
        border-top: 0;
        padding-top: 0;
        padding-bottom: 0;

        > * {
          opacity: 0;
          margin: 0 !important;
        }
      }
    }

    &__context-selector {
      @include scrollbar();

      white-space: nowrap;
      overflow-x: auto;
      // Add margin above horizontal scrollbar.
      padding-bottom: 32px;

      @include breakpoint($until: lg) {
        padding: 24px 20px;

        &::-webkit-scrollbar-thumb {
          border-left: 20px solid $color-white;
          border-right: 20px solid $color-white;
        }
      }
    }

    &__context-button {
      line-height: 18px;
      padding: 7px 12px;
      border-radius: 12px;
      font-size: 14px;
      font-weight: $font-weight-medium;
      color: $color-text-extra-dark;
      border: 1.5px solid $color-neutral-500;
      transition: color .1s,
                  border .1s,
                  background .1s;
      text-transform: lowercase;

      @include breakpoint(lg) {
        font-weight: $font-weight-semi-bold;
      }
      + #{$root}__context-button {
        margin-left: 10px;
      }

      &:hover {
        border-color: $color-cloud;
      }

      &.is-active {
        background: $color-brand-primary-lilac;
        border-color: $color-brand-primary-lilac;
        color: $color-neutral-900;
      }
    }

    &.is-expanded {
      @include breakpoint($until: lg) {
        height: 64px;
        #{$root}__container {
          overflow-y: scroll;
          padding: 20px 0;
          background: rgba($color-white, 1);
          position: fixed;
          transform: translateY(-70px);
          width: 100%;
          height: 100%;
          z-index: get-z-index(overlay);
          transition: transform .25s ease 0s,
                      background .25s ease 0s,
                      width .25s ease 0s,
                      height .25s ease 0s,
                      padding .25s ease 0s;
        }
        #{$root}__header {
          padding: 0 20px;
        }
        #{$root}__cancel {
          max-width: 78px;
          opacity: 1;
          padding-left: 12px !important;
          padding-right: 8px !important;
          transition: opacity .25s ease .25s,
                      max-width .25s ease .25s,
                      padding .25s ease .25s;
        }
        #{$root}__expansion {
          opacity: 1;
          position: static;
          left: 0;
          transition: opacity .25s ease 0s;
          overflow-x: visible;
          height: calc(100% - 24px);
          visibility: visible;
        }
      }

      @include breakpoint(lg) {
        #{$root}__field {
          max-width: 531px;
          box-shadow: 0 0 10px rgba($color-black, .03);
          transition: max-width 0s ease 0s,
                      box-shadow .25s ease 0s;

          .o-input {
            border-color: $color-primary;
            transition: border-radius .25s ease 0s;

            &:focus,
            &.is-invalid:focus {
              box-shadow: none;
            }
          }
        }

        #{$root}__expansion {
          border-color: $color-swiss-coffee;
          border-top-color: $color-pampas;
          box-shadow: 0 4px 10px rgba($color-black, .03);
          opacity: 1;
          overflow: auto;
          visibility: visible;
          transition: opacity 0s ease 0s,
                      box-shadow .25s ease 0s,
                      visibility 0s ease 0s;
        }
      }

      @include breakpoint($until: lg) {
        #{$root}__field .o-input::placeholder {
          opacity: 0;
        }
      }
    }

    .has-marginless-header & {
      padding-bottom: 0;
    }
  }
</style>
